import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormGroup, FormControlLabel, Checkbox, Stack, Alert,
  CircularProgress, Tabs, Tab, InputAdornment, Tooltip, Card, CardContent,
  Grid, Avatar, MenuItem, Select, FormControl, InputLabel, Snackbar, Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldIcon from '@mui/icons-material/Shield';
import BadgeIcon from '@mui/icons-material/Badge';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import { useRoster } from '../RosterContext';
import { SpecialUser, RosterUserSummary, AdminUsersResponse, AccessRequest, UserActivityStats, EmailDispatchRecord } from '../types';
import { fetchAdminUsers, addAdminUser, updateAdminUser, deleteAdminUser, approveAdminAccessRequest, rejectAdminAccessRequest, fetchEmailDispatches, triggerAdminDispatch } from '../utils/apiClient';
import { logEvent } from '../analytics';

export function formatLastSeen(lastSeen?: string | null): { text: string; full: string; isRecent: boolean; isToday: boolean } {
  if (!lastSeen) {
    return { text: 'Never', full: 'No recorded activity yet', isRecent: false, isToday: false };
  }
  const date = new Date(lastSeen);
  if (isNaN(date.getTime())) {
    return { text: 'Never', full: 'No recorded activity yet', isRecent: false, isToday: false };
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const full = date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  if (diffMins < 1) {
    return { text: 'Just now', full, isRecent: true, isToday: true };
  }
  if (diffMins < 60) {
    return { text: `${diffMins}m ago`, full, isRecent: true, isToday: true };
  }
  if (diffHours < 24) {
    return { text: `${diffHours}h ago`, full, isRecent: true, isToday: date.getDate() === now.getDate() };
  }
  if (diffDays === 1) {
    return { text: 'Yesterday', full, isRecent: true, isToday: false };
  }
  if (diffDays < 7) {
    return { text: `${diffDays}d ago`, full, isRecent: true, isToday: false };
  }
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return { text: `${weeks}w ago`, full, isRecent: false, isToday: false };
  }
  return {
    text: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
    full,
    isRecent: false,
    isToday: false
  };
}

export default function AdminPage() {
  const { userProfile, isAdmin, refetchRosters } = useRoster();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminUsersResponse | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [dialogEmail, setDialogEmail] = useState('');
  const [dialogName, setDialogName] = useState('');
  const [dialogNotes, setDialogNotes] = useState('');
  const [dialogIsAdmin, setDialogIsAdmin] = useState(false);
  const [dialogIsManager, setDialogIsManager] = useState(false);
  const [dialogIsViewer, setDialogIsViewer] = useState(true);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<SpecialUser | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Access Request Approve Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [requestToApprove, setRequestToApprove] = useState<AccessRequest | null>(null);
  const [approveIsAdmin, setApproveIsAdmin] = useState(false);
  const [approveIsManager, setApproveIsManager] = useState(false);
  const [approveIsViewer, setApproveIsViewer] = useState(true);
  const [approveSaving, setApproveSaving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  // Access Request Reject Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState<AccessRequest | null>(null);
  const [rejectSaving, setRejectSaving] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  // Snackbar notifications
  const [snackbarMessage, setSnackbarMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);

  // Email delivery & health states
  const [emailDispatches, setEmailDispatches] = useState<EmailDispatchRecord[]>([]);
  const [dailySubscribersCount, setDailySubscribersCount] = useState(0);
  const [weeklySubscribersCount, setWeeklySubscribersCount] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);
  const [triggeringDaily, setTriggeringDaily] = useState(false);
  const [triggeringWeekly, setTriggeringWeekly] = useState(false);
  const [selectedDispatchForLogs, setSelectedDispatchForLogs] = useState<EmailDispatchRecord | null>(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, emailRes] = await Promise.all([
        fetchAdminUsers(),
        typeof fetchEmailDispatches === 'function'
          ? Promise.resolve(fetchEmailDispatches(30)).catch(err => {
              console.warn('Could not load email dispatches:', err);
              return { dispatches: [], dailySubscribers: 0, weeklySubscribers: 0 };
            })
          : Promise.resolve({ dispatches: [], dailySubscribers: 0, weeklySubscribers: 0 })
      ]);
      setData(res);
      setEmailDispatches(emailRes.dispatches || []);
      setDailySubscribersCount(emailRes.dailySubscribers || 0);
      setWeeklySubscribersCount(emailRes.weeklySubscribers || 0);
    } catch (err: any) {
      console.error('Failed to load admin user data:', err);
      setError(err.message || 'Failed to load user permissions data from backend.');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshEmailDispatches = async () => {
    setEmailLoading(true);
    try {
      const emailRes = await fetchEmailDispatches(30);
      setEmailDispatches(emailRes.dispatches || []);
      setDailySubscribersCount(emailRes.dailySubscribers || 0);
      setWeeklySubscribersCount(emailRes.weeklySubscribers || 0);
      setSnackbarMessage({ text: 'Email delivery history refreshed', severity: 'info' });
    } catch (err: any) {
      console.error('Failed to refresh email dispatches:', err);
      setSnackbarMessage({ text: 'Failed to refresh email delivery history', severity: 'error' });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTriggerDispatch = async (jobType: 'daily' | 'weekly', force: boolean = true) => {
    if (jobType === 'daily') setTriggeringDaily(true);
    else setTriggeringWeekly(true);
    try {
      const res = await triggerAdminDispatch(jobType, force);
      const resData = res.result || {};
      setSnackbarMessage({
        text: `Triggered ${jobType === 'daily' ? 'Daily Reminders' : 'Weekly Updates'}: ${resData.succeeded ?? 0} sent, ${resData.skipped ?? 0} skipped, ${resData.failed ?? 0} failed.`,
        severity: (resData.failed && resData.failed > 0) ? 'error' : 'success',
      });
      await refreshEmailDispatches();
    } catch (err: any) {
      setSnackbarMessage({
        text: `Failed to trigger ${jobType} dispatch: ${err.message}`,
        severity: 'error',
      });
    } finally {
      if (jobType === 'daily') setTriggeringDaily(false);
      else setTriggeringWeekly(false);
    }
  };

  useEffect(() => {
    loadData();
    logEvent('view_admin_page');
  }, [loadData]);

  // Open add dialog
  const handleOpenAddDialog = (prefillEmail: string = '', prefillName: string = '') => {
    setIsEditingExisting(false);
    setDialogEmail(prefillEmail);
    setDialogName(prefillName);
    setDialogNotes('');
    setDialogIsAdmin(false);
    setDialogIsManager(false);
    setDialogIsViewer(true);
    setDialogError(null);
    setEditDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = (user: SpecialUser) => {
    setIsEditingExisting(true);
    setDialogEmail(user.email);
    setDialogName(user.name || '');
    setDialogNotes(user.notes || '');
    setDialogIsAdmin(user.roles.includes('admin'));
    setDialogIsManager(user.roles.includes('manager') || user.roles.includes('team_manager'));
    setDialogIsViewer(user.roles.includes('viewer') || user.roles.length === 0);
    setDialogError(null);
    setEditDialogOpen(true);
  };

  // Save special user
  const handleSaveUser = async () => {
    if (!dialogEmail || !dialogEmail.includes('@')) {
      setDialogError('Please enter a valid email address.');
      return;
    }

    const roles: string[] = [];
    if (dialogIsAdmin) roles.push('admin');
    if (dialogIsManager) roles.push('manager');
    if (dialogIsViewer || dialogIsAdmin || dialogIsManager) roles.push('viewer');

    if (roles.length === 0) {
      setDialogError('Please select at least one role for this user.');
      return;
    }

    setDialogSaving(true);
    setDialogError(null);
    try {
      if (isEditingExisting) {
        await updateAdminUser(dialogEmail, {
          name: dialogName.trim(),
          roles,
          notes: dialogNotes.trim()
        });
        setSnackbarMessage({ text: `Updated permissions for ${dialogEmail}`, severity: 'success' });
      } else {
        await addAdminUser({
          email: dialogEmail.trim().toLowerCase(),
          name: dialogName.trim(),
          roles,
          notes: dialogNotes.trim()
        });
        setSnackbarMessage({ text: `Added user ${dialogEmail}`, severity: 'success' });
      }
      setEditDialogOpen(false);
      await loadData();
      await refetchRosters();
    } catch (err: any) {
      setDialogError(err.message || 'Failed to save user permissions.');
    } finally {
      setDialogSaving(false);
    }
  };

  // Open delete dialog
  const handleOpenDeleteDialog = (user: SpecialUser) => {
    setUserToDelete(user);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleteSaving(true);
    setDeleteError(null);
    try {
      await deleteAdminUser(userToDelete.email);
      setSnackbarMessage({ text: `Removed special access for ${userToDelete.email}`, severity: 'success' });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await loadData();
      await refetchRosters();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to remove user.');
    } finally {
      setDeleteSaving(false);
    }
  };

  // Open Approve Request dialog
  const handleOpenApproveDialog = (req: AccessRequest) => {
    setRequestToApprove(req);
    setApproveIsAdmin(false);
    setApproveIsManager(false);
    setApproveIsViewer(true);
    setApproveError(null);
    setApproveDialogOpen(true);
  };

  // Submit Approval
  const handleConfirmApprove = async () => {
    if (!requestToApprove) return;
    const roles: string[] = [];
    if (approveIsAdmin) roles.push('admin');
    if (approveIsManager) roles.push('manager');
    if (approveIsViewer || approveIsAdmin || approveIsManager) roles.push('viewer');

    setApproveSaving(true);
    setApproveError(null);
    try {
      await approveAdminAccessRequest(requestToApprove.email, roles);
      setSnackbarMessage({ text: `Approved access for ${requestToApprove.name || requestToApprove.email}`, severity: 'success' });
      setApproveDialogOpen(false);
      setRequestToApprove(null);
      await loadData();
      await refetchRosters();
    } catch (err: any) {
      setApproveError(err.message || 'Failed to approve access request.');
    } finally {
      setApproveSaving(false);
    }
  };

  // Open Reject Request dialog
  const handleOpenRejectDialog = (req: AccessRequest) => {
    setRequestToReject(req);
    setRejectError(null);
    setRejectDialogOpen(true);
  };

  // Submit Rejection
  const handleConfirmReject = async () => {
    if (!requestToReject) return;
    setRejectSaving(true);
    setRejectError(null);
    try {
      await rejectAdminAccessRequest(requestToReject.email);
      setSnackbarMessage({ text: `Rejected access request from ${requestToReject.email}`, severity: 'info' });
      setRejectDialogOpen(false);
      setRequestToReject(null);
      await loadData();
    } catch (err: any) {
      setRejectError(err.message || 'Failed to reject access request.');
    } finally {
      setRejectSaving(false);
    }
  };

  // Quick promotion from Roster tab
  const handlePromoteRosterUser = (rosterUser: RosterUserSummary, roleToGrant: 'manager' | 'admin') => {
    const existingSpecial = data?.specialUsers.find(u => u.email.toLowerCase() === rosterUser.email.toLowerCase());
    if (existingSpecial) {
      setIsEditingExisting(true);
      setDialogEmail(existingSpecial.email);
      setDialogName(existingSpecial.name || rosterUser.name);
      setDialogNotes(existingSpecial.notes || '');
      setDialogIsAdmin(roleToGrant === 'admin' ? true : existingSpecial.roles.includes('admin'));
      setDialogIsManager(roleToGrant === 'manager' ? true : existingSpecial.roles.includes('manager'));
      setDialogIsViewer(true);
    } else {
      setIsEditingExisting(false);
      setDialogEmail(rosterUser.email);
      setDialogName(rosterUser.name);
      setDialogNotes(rosterUser.rosterRoles.join(', '));
      setDialogIsAdmin(roleToGrant === 'admin');
      setDialogIsManager(roleToGrant === 'manager');
      setDialogIsViewer(true);
    }
    setDialogError(null);
    setEditDialogOpen(true);
  };

  // User activity summary
  const activitySummary = useMemo(() => {
    const summary = { activeToday: 0, active7Days: 0, totalVisits: 0, totalUsersWithVisits: 0 };
    if (!data) return summary;

    const seenEmails = new Set<string>();
    const allActivities: UserActivityStats[] = [];

    if (data.userActivity) {
      for (const [em, act] of Object.entries(data.userActivity)) {
        if (!seenEmails.has(em)) {
          seenEmails.add(em);
          allActivities.push(act);
        }
      }
    }

    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const sevenDays = 7 * oneDay;

    for (const act of allActivities) {
      const visits = act.totalVisits || 0;
      summary.totalVisits += visits;
      if (visits > 0) summary.totalUsersWithVisits++;

      if (act.lastSeen) {
        const time = new Date(act.lastSeen).getTime();
        if (!isNaN(time)) {
          if (now - time <= oneDay) summary.activeToday++;
          if (now - time <= sevenDays) summary.active7Days++;
        }
      }
    }

    return summary;
  }, [data]);

  // Filtered Special Users
  const filteredSpecialUsers = useMemo(() => {
    if (!data?.specialUsers) return [];
    const list = data.specialUsers.filter(user => {
      if (!user) return false;
      const q = searchQuery.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const name = (user.name || '').toLowerCase();
      const notes = (user.notes || '').toLowerCase();
      const matchesSearch =
        !q ||
        email.includes(q) ||
        name.includes(q) ||
        notes.includes(q);

      if (!matchesSearch) return false;

      const roles = user.roles || [];
      if (roleFilter === 'admin' && !roles.includes('admin')) return false;
      if (roleFilter === 'manager' && !(roles.includes('manager') || roles.includes('team_manager'))) return false;
      if (roleFilter === 'viewer' && (roles.includes('admin') || roles.includes('manager'))) return false;

      const activity = user.activity || (data?.userActivity && data.userActivity[email]);
      const lastSeenTime = activity?.lastSeen ? new Date(activity.lastSeen).getTime() : 0;
      const now = Date.now();
      const hasVisits = Boolean(activity && (activity.totalVisits || 0) > 0);

      if (activityFilter === 'active_7d' && (now - lastSeenTime > 7 * 24 * 60 * 60 * 1000 || lastSeenTime === 0)) return false;
      if (activityFilter === 'active_ever' && !hasVisits) return false;
      if (activityFilter === 'never' && hasVisits) return false;

      return true;
    });

    return list.sort((a, b) => {
      const actA = a.activity || (data?.userActivity && data.userActivity[a.email.toLowerCase()]);
      const actB = b.activity || (data?.userActivity && data.userActivity[b.email.toLowerCase()]);
      if (sortBy === 'last_seen') {
        const timeA = actA?.lastSeen ? new Date(actA.lastSeen).getTime() : 0;
        const timeB = actB?.lastSeen ? new Date(actB.lastSeen).getTime() : 0;
        return timeB - timeA;
      }
      if (sortBy === 'visits') {
        return (actB?.totalVisits || 0) - (actA?.totalVisits || 0);
      }
      if (sortBy === 'days') {
        return (actB?.daysVisitedCount || 0) - (actA?.daysVisitedCount || 0);
      }
      const nameA = (a.name || a.email).toLowerCase();
      const nameB = (b.name || b.email).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data?.specialUsers, data?.userActivity, searchQuery, roleFilter, activityFilter, sortBy]);

  // Filtered Access Requests
  const filteredAccessRequests = useMemo(() => {
    if (!data?.accessRequests) return [];
    return data.accessRequests.filter(req => {
      if (!req) return false;
      const q = searchQuery.toLowerCase();
      const email = (req.email || '').toLowerCase();
      const name = (req.name || '').toLowerCase();
      const reason = (req.reason || '').toLowerCase();
      const matchesSearch =
        !q ||
        email.includes(q) ||
        name.includes(q) ||
        reason.includes(q);

      if (!matchesSearch) return false;

      if (requestStatusFilter === 'all') return true;
      if (requestStatusFilter === req.status) return true;
      return false;
    });
  }, [data?.accessRequests, searchQuery, requestStatusFilter]);

  // Filtered Roster Users
  const filteredRosterUsers = useMemo(() => {
    if (!data?.rosterUsers) return [];
    const list = data.rosterUsers.filter(u => {
      const q = searchQuery.toLowerCase();
      const email = u.email.toLowerCase();
      const matchesSearch = !q || (
        email.includes(q) ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        u.rosterRoles.some(r => r.toLowerCase().includes(q)) ||
        u.associatedPlayers.some(p => p.playerName.toLowerCase().includes(q)) ||
        u.seasons.some(s => s.toLowerCase().includes(q))
      );
      if (!matchesSearch) return false;

      const activity = u.activity || (data?.userActivity && data.userActivity[email]);
      const lastSeenTime = activity?.lastSeen ? new Date(activity.lastSeen).getTime() : 0;
      const now = Date.now();
      const hasVisits = Boolean(activity && (activity.totalVisits || 0) > 0);

      if (activityFilter === 'active_7d' && (now - lastSeenTime > 7 * 24 * 60 * 60 * 1000 || lastSeenTime === 0)) return false;
      if (activityFilter === 'active_ever' && !hasVisits) return false;
      if (activityFilter === 'never' && hasVisits) return false;

      return true;
    });

    return list.sort((a, b) => {
      const actA = a.activity || (data?.userActivity && data.userActivity[a.email.toLowerCase()]);
      const actB = b.activity || (data?.userActivity && data.userActivity[b.email.toLowerCase()]);
      if (sortBy === 'last_seen') {
        const timeA = actA?.lastSeen ? new Date(actA.lastSeen).getTime() : 0;
        const timeB = actB?.lastSeen ? new Date(actB.lastSeen).getTime() : 0;
        return timeB - timeA;
      }
      if (sortBy === 'visits') {
        return (actB?.totalVisits || 0) - (actA?.totalVisits || 0);
      }
      if (sortBy === 'days') {
        return (actB?.daysVisitedCount || 0) - (actA?.daysVisitedCount || 0);
      }
      const nameA = (a.name || a.email).toLowerCase();
      const nameB = (b.name || b.email).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data?.rosterUsers, data?.userActivity, searchQuery, activityFilter, sortBy]);

  // Look up roster info for a given email
  const getRosterInfoForEmail = (email: string): RosterUserSummary | undefined => {
    if (!data?.rosterUsers) return undefined;
    return data.rosterUsers.find(r => r.email.toLowerCase() === email.toLowerCase());
  };

  const pendingRequestsCount = useMemo(() => {
    return data?.accessRequests?.filter(r => r.status === 'pending').length || 0;
  }, [data?.accessRequests]);

  const emailFailureCount = useMemo(() => {
    return emailDispatches.filter(d => d.status === 'failed' || d.status === 'partial_failure').length;
  }, [emailDispatches]);

  const filteredDispatches = useMemo(() => {
    if (!emailDispatches) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return emailDispatches;
    return emailDispatches.filter(d => {
      const batch = (d.batchId || '').toLowerCase();
      const job = (d.jobType || '').toLowerCase();
      const status = (d.status || '').toLowerCase();
      const hasErrorMatch = d.errors?.some(e =>
        (e.recipient || '').toLowerCase().includes(q) ||
        (e.error || '').toLowerCase().includes(q)
      );
      return batch.includes(q) || job.includes(q) || status.includes(q) || Boolean(hasErrorMatch);
    });
  }, [emailDispatches, searchQuery]);

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <LockPersonIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You do not have administrator permissions to view or manage user access roles.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h4" fontWeight="bold">
              Access & Role Management
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage administrators, grant Team Manager schedule access, and add special view permissions.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadData()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => handleOpenAddDialog()}
            disableElevation
          >
            Add Special User
          </Button>
        </Stack>
      </Box>

      {/* Metric Cards */}
      {data && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ xs: 6, sm: 4, md: pendingRequestsCount > 0 ? 2 : 2.4 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'error.light', color: 'error.dark', width: 40, height: 40 }}>
                    <ShieldIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {data.adminEmails.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Administrators
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: pendingRequestsCount > 0 ? 2 : 2.4 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'info.light', color: 'info.dark', width: 40, height: 40 }}>
                    <SupervisorAccountIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {data.managerEmails.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Team Managers
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: pendingRequestsCount > 0 ? 2 : 2.4 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark', width: 40, height: 40 }}>
                    <VisibilityIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {data.specialUsers.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Managed Access
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: pendingRequestsCount > 0 ? 2 : 2.4 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 40, height: 40 }}>
                    <GroupIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {data.rosterUsers.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Roster Members
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: pendingRequestsCount > 0 ? 2 : 2.4 }}>
            <Tooltip title={`Total visits tracked: ${activitySummary.totalVisits} across ${activitySummary.totalUsersWithVisits} registered users`}>
              <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper', height: '100%' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark', width: 40, height: 40 }}>
                      <AccessTimeIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {activitySummary.active7Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
                        Active (7 Days)
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>

          {pendingRequestsCount > 0 && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card 
                variant="outlined" 
                onClick={() => setCurrentTab(1)}
                sx={{ 
                  borderRadius: 2, 
                  bgcolor: '#fef3c7', 
                  borderColor: '#f59e0b', 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease-in-out',
                  '&:hover': { transform: 'scale(1.02)' }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: '#f59e0b', color: '#ffffff', width: 40, height: 40 }}>
                      <MarkEmailUnreadIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="warning.dark">
                        {pendingRequestsCount}
                      </Typography>
                      <Typography variant="caption" color="warning.dark" fontWeight={600}>
                        Pending Requests
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Role Explanations Banner */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f8fafd' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <ShieldIcon color="primary" sx={{ mt: 0.2 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">Admin Role</Typography>
              <Typography variant="caption" color="text.secondary">
                Full system control: user management, cross-season trends, player stats, puckle, and dev toggle.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <SupervisorAccountIcon color="info" sx={{ mt: 0.2 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">Team Manager Role</Typography>
              <Typography variant="caption" color="text.secondary">
                Grants visibility to schedule conflict detection, ice time issues, and displays Team Manager badge.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <VisibilityIcon color="success" sx={{ mt: 0.2 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">View Access</Typography>
              <Typography variant="caption" color="text.secondary">
                Enables portal login and viewing for users not listed on any season roster (e.g. family, club staff).
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={() => loadData()}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Main Tabs Container */}
      <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={currentTab}
          onChange={(_, newVal) => setCurrentTab(newVal)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, bgcolor: 'background.paper' }}
        >
          <Tab 
            icon={<BadgeIcon fontSize="small" />} 
            iconPosition="start" 
            label={`Special & Managed Users (${data?.specialUsers?.length ?? 0})`} 
          />
          <Tab 
            icon={
              <Badge badgeContent={pendingRequestsCount} color="warning" sx={{ '& .MuiBadge-badge': { right: -4, top: 4 } }}>
                <MarkEmailUnreadIcon fontSize="small" />
              </Badge>
            } 
            iconPosition="start" 
            label={`Access Requests (${data?.accessRequests?.length ?? 0})`} 
          />
          <Tab 
            icon={<GroupIcon fontSize="small" />} 
            iconPosition="start" 
            label={`Roster Directory & Roles (${data?.rosterUsers?.length ?? 0})`} 
          />
          <Tab 
            icon={
              <Badge badgeContent={emailFailureCount} color="error" sx={{ '& .MuiBadge-badge': { right: -4, top: 4 } }}>
                <ForwardToInboxIcon fontSize="small" />
              </Badge>
            } 
            iconPosition="start" 
            label={`Email Delivery & Health (${emailDispatches.length})`} 
          />
        </Tabs>

        {/* Filter / Search Bar */}
        <Box sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, bgcolor: '#fafafa', borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={
              currentTab === 0
                ? "Search by email, name, or notes..."
                : currentTab === 1
                ? "Search access requests by name, email, or reason..."
                : currentTab === 2
                ? "Search parents, coaches, players, or emails..."
                : "Search dispatches by batch, error, recipient, or job..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 220 }, bgcolor: 'background.paper' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {currentTab === 0 && (
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
              <InputLabel id="role-filter-label">Filter by Role</InputLabel>
              <Select
                labelId="role-filter-label"
                value={roleFilter}
                label="Filter by Role"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="admin">Administrators</MenuItem>
                <MenuItem value="manager">Team Managers</MenuItem>
                <MenuItem value="viewer">View Access Only</MenuItem>
              </Select>
            </FormControl>
          )}
          {(currentTab === 0 || currentTab === 2) && (
            <FormControl size="small" sx={{ minWidth: 165, bgcolor: 'background.paper' }}>
              <InputLabel id="activity-filter-label">Activity Status</InputLabel>
              <Select
                labelId="activity-filter-label"
                value={activityFilter}
                label="Activity Status"
                onChange={(e) => setActivityFilter(e.target.value)}
              >
                <MenuItem value="all">All Activity</MenuItem>
                <MenuItem value="active_7d">Active (Past 7 Days)</MenuItem>
                <MenuItem value="active_ever">Active (Ever Visited)</MenuItem>
                <MenuItem value="never">Never Logged In</MenuItem>
              </Select>
            </FormControl>
          )}
          {(currentTab === 0 || currentTab === 2) && (
            <FormControl size="small" sx={{ minWidth: 165, bgcolor: 'background.paper' }}>
              <InputLabel id="sort-filter-label">Sort By</InputLabel>
              <Select
                labelId="sort-filter-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Name (A-Z)</MenuItem>
                <MenuItem value="last_seen">Recently Active</MenuItem>
                <MenuItem value="visits">Most Visits</MenuItem>
                <MenuItem value="days">Most Days Visited</MenuItem>
              </Select>
            </FormControl>
          )}
          {currentTab === 1 && (
            <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'background.paper' }}>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={requestStatusFilter}
                label="Filter by Status"
                onChange={(e) => setRequestStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Requests</MenuItem>
                <MenuItem value="pending">Pending Review</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Loading Indicator */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* TAB 0: Special Users */}
            {currentTab === 0 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f4f6f8' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User / Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Assigned Roles</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Roster Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Active</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Site Usage</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes / Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSpecialUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                          No special users match the current search or filter.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSpecialUsers.map((u) => {
                        const isSelf = u.email.toLowerCase() === userProfile?.email.toLowerCase();
                        const isOnlyAdmin = u.roles.includes('admin') && (data?.adminEmails.length === 1);
                        const rosterInfo = getRosterInfoForEmail(u.email);

                        const activity = u.activity || (data?.userActivity && data.userActivity[u.email.toLowerCase()]);
                        const lastSeenInfo = formatLastSeen(activity?.lastSeen);
                        const totalVisits = activity?.totalVisits || 0;
                        const daysVisited = activity?.daysVisitedCount || (activity?.visitedDays?.length || 0);
                        const hasActivity = Boolean(activity && activity.lastSeen && totalVisits > 0);

                        return (
                          <TableRow key={u.email} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: u.roles.includes('admin') ? 'primary.main' : 'grey.400', fontSize: '0.9rem' }}>
                                  {u.name ? u.name.charAt(0).toUpperCase() : u.email.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography component="div" variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
                                    {u.name || u.email.split('@')[0]}
                                    {isSelf && (
                                      <Chip label="You" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {u.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {u.roles.includes('admin') && (
                                  <Chip
                                    icon={<ShieldIcon sx={{ '&&': { fontSize: 14 } }} />}
                                    label="Admin"
                                    color="primary"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                                {(u.roles.includes('manager') || u.roles.includes('team_manager')) && (
                                  <Chip
                                    icon={<SupervisorAccountIcon sx={{ '&&': { fontSize: 14 } }} />}
                                    label="Team Manager"
                                    color="info"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                                {u.roles.includes('viewer') && !u.roles.includes('admin') && !u.roles.includes('manager') && (
                                  <Chip
                                    icon={<VisibilityIcon sx={{ '&&': { fontSize: 14 } }} />}
                                    label="View Access"
                                    color="default"
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {rosterInfo ? (
                                <Box>
                                  <Chip
                                    label="On Roster"
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem', mb: 0.5 }}
                                  />
                                  <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                    {rosterInfo.rosterRoles.join(', ')}
                                  </Typography>
                                </Box>
                              ) : (
                                <Chip
                                  label="Non-Roster User"
                                  variant="outlined"
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem', color: 'text.secondary' }}
                                />
                              )}
                            </TableCell>

                            {/* Last Active Column */}
                            <TableCell>
                              {hasActivity ? (
                                <Tooltip title={lastSeenInfo.full} arrow placement="top">
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: lastSeenInfo.isRecent ? 'success.main' : 'text.disabled',
                                        flexShrink: 0
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight={lastSeenInfo.isRecent ? 600 : 400}>
                                      {lastSeenInfo.text}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: 'grey.300',
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Never
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>

                            {/* Site Usage Column */}
                            <TableCell>
                              {hasActivity ? (
                                <Tooltip
                                  title={`Total site visits: ${totalVisits} | Unique days visited: ${daysVisited}${activity?.visitedDays?.length ? `\nDates: ${activity.visitedDays.slice(-5).join(', ')}` : ''}`}
                                  arrow
                                  placement="top"
                                >
                                  <Box sx={{ cursor: 'pointer' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {totalVisits} {totalVisits === 1 ? 'visit' : 'visits'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {daysVisited} {daysVisited === 1 ? 'day' : 'days'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  0 visits (0 days)
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 220 }}>
                                {u.notes || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Tooltip title="Edit roles and details">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenEditDialog(u)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={isOnlyAdmin ? "Cannot delete the only admin" : "Remove user access"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={isOnlyAdmin}
                                      onClick={() => handleOpenDeleteDialog(u)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 1: Access Requests */}
            {currentTab === 1 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f4f6f8' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Requester</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reason / Relationship</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Submitted Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccessRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                          No access requests found matching the current search or filter.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAccessRequests.map((req) => {
                        const isPending = req.status === 'pending';
                        const isApproved = req.status === 'approved';
                        const isRejected = req.status === 'rejected';

                        const reqEmail = req.email || '';
                        const reqName = req.name || '';
                        const avatarLetter = reqName ? reqName.charAt(0).toUpperCase() : (reqEmail ? reqEmail.charAt(0).toUpperCase() : '?');
                        const displayName = reqName || (reqEmail.includes('@') ? reqEmail.split('@')[0] : reqEmail || 'Unknown');

                        return (
                          <TableRow key={req.id || reqEmail || Math.random().toString()} hover sx={{ bgcolor: isPending ? '#fffbeb' : undefined }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: isPending ? 'warning.main' : isApproved ? 'success.main' : 'grey.400', fontSize: '0.9rem' }}>
                                  {avatarLetter}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {displayName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {reqEmail}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 300, whiteSpace: 'pre-wrap' }}>
                                {req.reason || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {req.createdAt ? new Date(req.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isPending && (
                                <Chip
                                  icon={<HourglassEmptyIcon sx={{ '&&': { fontSize: 14 } }} />}
                                  label="Pending Review"
                                  color="warning"
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              {isApproved && (
                                <Box>
                                  <Chip
                                    icon={<CheckCircleIcon sx={{ '&&': { fontSize: 14 } }} />}
                                    label="Approved"
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                  />
                                  {req.reviewedBy && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      By {req.reviewedBy}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {isRejected && (
                                <Box>
                                  <Chip
                                    icon={<CancelIcon sx={{ '&&': { fontSize: 14 } }} />}
                                    label="Rejected"
                                    color="default"
                                    size="small"
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                  />
                                  {req.reviewedBy && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      By {req.reviewedBy}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {isPending ? (
                                  <>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      startIcon={<CheckCircleIcon />}
                                      onClick={() => handleOpenApproveDialog(req)}
                                      disableElevation
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={<CancelIcon />}
                                      onClick={() => handleOpenRejectDialog(req)}
                                    >
                                      Reject
                                    </Button>
                                  </>
                                ) : isRejected ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => handleOpenApproveDialog(req)}
                                  >
                                    Re-approve
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                      const existing = data?.specialUsers.find(u => u.email.toLowerCase() === req.email.toLowerCase());
                                      if (existing) {
                                        handleOpenEditDialog(existing);
                                      } else {
                                        handleOpenAddDialog(req.email, req.name);
                                      }
                                    }}
                                  >
                                    Edit Roles
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 2: Roster Directory */}
            {currentTab === 2 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f4f6f8' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Roster Member</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Roster Role & Player</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Seasons</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Active</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Site Usage</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Current Special Roles</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Role Management</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRosterUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                          No roster members found matching your search.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRosterUsers.map((ru) => {
                        const existingSpecial = data?.specialUsers.find(
                          u => u.email.toLowerCase() === ru.email.toLowerCase()
                        );
                        const isManager = existingSpecial?.roles.includes('manager') || ru.isTeamManagerOnRoster;
                        const isAdminUser = existingSpecial?.roles.includes('admin');

                        const activity = ru.activity || (data?.userActivity && data.userActivity[ru.email.toLowerCase()]);
                        const lastSeenInfo = formatLastSeen(activity?.lastSeen);
                        const totalVisits = activity?.totalVisits || 0;
                        const daysVisited = activity?.daysVisitedCount || (activity?.visitedDays?.length || 0);
                        const hasActivity = Boolean(activity && activity.lastSeen && totalVisits > 0);

                        return (
                          <TableRow key={ru.email} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.300', color: 'text.primary', fontSize: '0.9rem' }}>
                                  {ru.name ? ru.name.charAt(0).toUpperCase() : ru.email.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {ru.name || ru.email.split('@')[0]}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {ru.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Stack direction="column" spacing={0.5}>
                                {ru.rosterRoles.map((role, idx) => (
                                  <Typography key={idx} variant="body2" color="text.secondary">
                                    {role}
                                  </Typography>
                                ))}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {ru.seasons.map(s => (
                                  <Chip key={s} label={s} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                ))}
                              </Stack>
                            </TableCell>

                            {/* Last Active Column */}
                            <TableCell>
                              {hasActivity ? (
                                <Tooltip title={lastSeenInfo.full} arrow placement="top">
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: lastSeenInfo.isRecent ? 'success.main' : 'text.disabled',
                                        flexShrink: 0
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight={lastSeenInfo.isRecent ? 600 : 400}>
                                      {lastSeenInfo.text}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: 'grey.300',
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Never
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>

                            {/* Site Usage Column */}
                            <TableCell>
                              {hasActivity ? (
                                <Tooltip
                                  title={`Total site visits: ${totalVisits} | Unique days visited: ${daysVisited}${activity?.visitedDays?.length ? `\nDates: ${activity.visitedDays.slice(-5).join(', ')}` : ''}`}
                                  arrow
                                  placement="top"
                                >
                                  <Box sx={{ cursor: 'pointer' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {totalVisits} {totalVisits === 1 ? 'visit' : 'visits'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {daysVisited} {daysVisited === 1 ? 'day' : 'days'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  0 visits (0 days)
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {isAdminUser && (
                                  <Chip
                                    icon={<ShieldIcon sx={{ '&&': { fontSize: 12 } }} />}
                                    label="Admin"
                                    color="primary"
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                  />
                                )}
                                {isManager && (
                                  <Chip
                                    icon={<SupervisorAccountIcon sx={{ '&&': { fontSize: 12 } }} />}
                                    label={ru.isTeamManagerOnRoster && !existingSpecial?.roles.includes('manager') ? "Manager (Roster)" : "Team Manager"}
                                    color="info"
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                  />
                                )}
                                {!isAdminUser && !isManager && (
                                  <Typography variant="caption" color="text.secondary">
                                    Standard Roster Access
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => handlePromoteRosterUser(ru, 'manager')}
                                >
                                  Manage Roles
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* TAB 3: EMAIL DELIVERY & HEALTH */}
        {currentTab === 3 && (
          <Box sx={{ p: 3 }}>
            {/* Top Summary Banner / Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 40, height: 40 }}>
                        <EmailIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight="bold">
                          {dailySubscribersCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Daily Reminder Subscribers
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark', width: 40, height: 40 }}>
                        <ForwardToInboxIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight="bold">
                          {weeklySubscribersCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Weekly Update Subscribers
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: 'info.light', color: 'info.dark', width: 40, height: 40 }}>
                        <ScheduleIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight="bold">
                          {emailDispatches.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Total Dispatches Recorded
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    height: '100%',
                    bgcolor: emailFailureCount > 0 ? '#fef2f2' : '#f0fdf4',
                    borderColor: emailFailureCount > 0 ? '#f87171' : '#86efac',
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          bgcolor: emailFailureCount > 0 ? 'error.main' : 'success.main',
                          color: '#ffffff',
                          width: 40,
                          height: 40,
                        }}
                      >
                        {emailFailureCount > 0 ? <WarningAmberIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color={emailFailureCount > 0 ? 'error.dark' : 'success.dark'}>
                          {emailFailureCount === 0 ? '100% Healthy' : `${emailFailureCount} Failed Runs`}
                        </Typography>
                        <Typography variant="caption" color={emailFailureCount > 0 ? 'error.dark' : 'success.dark'} fontWeight={500}>
                          {emailFailureCount === 0 ? 'No delivery errors detected' : 'Attention needed in recent runs'}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Actions Toolbar */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Recent Automated Dispatches & Delivery Telemetry
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={emailLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={refreshEmailDispatches}
                  disabled={emailLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={triggeringDaily ? <CircularProgress size={16} /> : <SendIcon />}
                  onClick={() => handleTriggerDispatch('daily', true)}
                  disabled={triggeringDaily || triggeringWeekly}
                >
                  {triggeringDaily ? 'Sending...' : 'Test Daily Reminders'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={triggeringWeekly ? <CircularProgress size={16} /> : <ForwardToInboxIcon />}
                  onClick={() => handleTriggerDispatch('weekly', true)}
                  disabled={triggeringDaily || triggeringWeekly}
                >
                  {triggeringWeekly ? 'Sending...' : 'Test Weekly Updates'}
                </Button>
              </Stack>
            </Stack>

            {/* Dispatches Table */}
            {filteredDispatches.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {searchQuery ? 'No email dispatches match your search query.' : 'No email dispatch runs recorded yet.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Dispatched Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Stream / Job</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Delivery Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Recipients</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unique Opens</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Errors</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDispatches.map((record) => {
                      const hasErrors = (record.errors && record.errors.length > 0) || record.failedCount > 0;
                      const isDaily = (record.jobType || '').toLowerCase().includes('daily');
                      return (
                        <TableRow key={record.batchId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {formatLastSeen(record.dispatchedAt).text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(record.dispatchedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} • {record.batchId.slice(0, 10)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={isDaily ? <SendIcon fontSize="small" /> : <ForwardToInboxIcon fontSize="small" />}
                              label={isDaily ? 'Daily Reminders' : 'Weekly Updates'}
                              color={isDaily ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                record.status === 'success'
                                  ? 'Delivered'
                                  : record.status === 'partial_failure'
                                  ? 'Partial Failure'
                                  : 'Failed'
                              }
                              color={
                                record.status === 'success'
                                  ? 'success'
                                  : record.status === 'partial_failure'
                                  ? 'warning'
                                  : 'error'
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              <strong>{record.succeededCount}</strong> sent / {record.totalAttempted} attempted
                            </Typography>
                            {record.skippedCount > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                ({record.skippedCount} skipped — no events)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <VisibilityIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight={600}>
                                {record.opensCount || 0}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {hasErrors ? (
                              <Chip
                                size="small"
                                color="error"
                                icon={<ErrorOutlineIcon fontSize="small" />}
                                label={`${record.errors?.length || record.failedCount} errors`}
                                onClick={() => {
                                  setSelectedDispatchForLogs(record);
                                  setLogDialogOpen(true);
                                }}
                                clickable
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                None
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSelectedDispatchForLogs(record);
                                  setLogDialogOpen(true);
                                }}
                              >
                                Details
                              </Button>
                              {hasErrors && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="warning"
                                  startIcon={<ReplayIcon fontSize="small" />}
                                  onClick={() => handleTriggerDispatch(isDaily ? 'daily' : 'weekly', true)}
                                  disabled={triggeringDaily || triggeringWeekly}
                                >
                                  Retry
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* ADD / EDIT USER DIALOG */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !dialogSaving && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {isEditingExisting ? 'Edit Special User Permissions' : 'Add Special User & Assign Roles'}
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              disabled={isEditingExisting}
              value={dialogEmail}
              onChange={(e) => setDialogEmail(e.target.value)}
              helperText={isEditingExisting ? "Email cannot be changed after creation." : "Enter user's Google or sign-in email address."}
            />

            <TextField
              label="Display Name / Label (Optional)"
              placeholder="e.g. Brandon Wuest, Grandpa Bob"
              fullWidth
              value={dialogName}
              onChange={(e) => setDialogName(e.target.value)}
            />

            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Assign Roles & Privileges
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dialogIsAdmin}
                      onChange={(e) => {
                        setDialogIsAdmin(e.target.checked);
                        if (e.target.checked) setDialogIsViewer(true);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        🛡️ Administrator
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Full access to admin panel, role management, trends, player stats, and dev toggles.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dialogIsManager}
                      onChange={(e) => {
                        setDialogIsManager(e.target.checked);
                        if (e.target.checked) setDialogIsViewer(true);
                      }}
                      color="info"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        📋 Team Manager
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Grants access to schedule conflict detection, ice time issues, and manager indicators.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dialogIsViewer || dialogIsAdmin || dialogIsManager}
                      disabled={dialogIsAdmin || dialogIsManager}
                      onChange={(e) => setDialogIsViewer(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        👁️ View Access (Allowlist)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Permits logging into team portal and viewing schedule/roster without being on any player roster.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </FormGroup>
            </Box>

            <TextField
              label="Notes / Reason (Optional)"
              placeholder="e.g. Grandma, Assistant Manager, League Coordinator"
              multiline
              rows={2}
              fullWidth
              value={dialogNotes}
              onChange={(e) => setDialogNotes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={dialogSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveUser}
            disabled={dialogSaving}
            startIcon={dialogSaving ? <CircularProgress size={16} /> : undefined}
            disableElevation
          >
            {dialogSaving ? 'Saving...' : 'Save User Roles'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* APPROVE ACCESS REQUEST DIALOG */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !approveSaving && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Approve Access Request
        </DialogTitle>
        <DialogContent dividers>
          {approveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {approveError}
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                {requestToApprove?.name || 'User'} ({requestToApprove?.email})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                "{requestToApprove?.reason || 'No reason provided'}"
              </Typography>
            </Box>

            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Assign Roles upon Approval
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approveIsAdmin}
                      onChange={(e) => {
                        setApproveIsAdmin(e.target.checked);
                        if (e.target.checked) setApproveIsViewer(true);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        🛡️ Administrator
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Full access to admin panel, role management, trends, player stats, and dev toggles.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approveIsManager}
                      onChange={(e) => {
                        setApproveIsManager(e.target.checked);
                        if (e.target.checked) setApproveIsViewer(true);
                      }}
                      color="info"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        📋 Team Manager
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Grants access to schedule conflict detection, ice time issues, and manager indicators.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approveIsViewer || approveIsAdmin || approveIsManager}
                      disabled={approveIsAdmin || approveIsManager}
                      onChange={(e) => setApproveIsViewer(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        👁️ View Access (Allowlist)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Permits logging into team portal and viewing schedule/roster without being on any player roster.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={approveSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmApprove}
            disabled={approveSaving}
            startIcon={approveSaving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            disableElevation
          >
            {approveSaving ? 'Approving...' : 'Approve & Grant Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* REJECT ACCESS REQUEST DIALOG */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !rejectSaving && setRejectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
          Reject Access Request?
        </DialogTitle>
        <DialogContent dividers>
          {rejectError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rejectError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reject the access request from <strong>{requestToReject?.name || requestToReject?.email}</strong> (<code>{requestToReject?.email}</code>)?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The user will remain blocked from accessing the team portal until approved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={rejectSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={rejectSaving}
            startIcon={rejectSaving ? <CircularProgress size={16} /> : <CancelIcon />}
            disableElevation
          >
            {rejectSaving ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteSaving && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
          Revoke Special Access?
        </DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            Are you sure you want to remove special access for <strong>{userToDelete?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will immediately remove their Admin, Team Manager, or special allowlist privileges.
            If this user is not registered on any team roster, they will no longer be able to log in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteSaving}
            startIcon={deleteSaving ? <CircularProgress size={16} /> : undefined}
            disableElevation
          >
            {deleteSaving ? 'Removing...' : 'Remove Special Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EMAIL DISPATCH DETAILS & LOGS DIALOG */}
      <Dialog
        open={logDialogOpen}
        onClose={() => setLogDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Dispatch Run Details & Telemetry
        </DialogTitle>
        <DialogContent dividers>
          {selectedDispatchForLogs && (
            <Stack spacing={2.5}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Batch ID</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                      {selectedDispatchForLogs.batchId}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Job Type</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedDispatchForLogs.jobType}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Dispatched At</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(selectedDispatchForLogs.dispatchedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Unique Opens</Typography>
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      {selectedDispatchForLogs.opensCount || 0} opens recorded
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip label={`Attempted: ${selectedDispatchForLogs.totalAttempted}`} variant="outlined" />
                <Chip label={`Succeeded: ${selectedDispatchForLogs.succeededCount}`} color="success" />
                <Chip label={`Skipped: ${selectedDispatchForLogs.skippedCount}`} variant="outlined" />
                <Chip
                  label={`Failed: ${selectedDispatchForLogs.failedCount}`}
                  color={selectedDispatchForLogs.failedCount > 0 ? 'error' : 'default'}
                />
              </Box>

              {(!selectedDispatchForLogs.errors || selectedDispatchForLogs.errors.length === 0) ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  All recipient emails were rendered and delivered successfully with zero exceptions.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                    Delivery Errors & Diagnostic Logs ({selectedDispatchForLogs.errors.length})
                  </Typography>
                  {selectedDispatchForLogs.errors.map((err, idx) => (
                    <Box key={idx} sx={{ p: 2, borderRadius: 2, border: '1px solid #fecaca', bgcolor: '#fef2f2' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="error.dark">
                          Recipient: {err.recipient}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {err.timestamp ? new Date(err.timestamp).toLocaleTimeString() : ''}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: err.traceback ? 1 : 0, color: '#991b1b' }}>
                        {err.error}
                      </Typography>
                      {err.traceback && (
                        <Accordion sx={{ bgcolor: '#ffffff', mt: 1, '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="caption" fontWeight="bold">View Python Traceback</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 1.5,
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                bgcolor: '#1e293b',
                                color: '#f8fafc',
                                borderRadius: 1,
                                overflowX: 'auto',
                                maxHeight: 250,
                              }}
                            >
                              {err.traceback}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {selectedDispatchForLogs && (selectedDispatchForLogs.failedCount > 0 || (selectedDispatchForLogs.errors && selectedDispatchForLogs.errors.length > 0)) && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<ReplayIcon />}
              onClick={() => {
                const isDaily = (selectedDispatchForLogs.jobType || '').toLowerCase().includes('daily');
                handleTriggerDispatch(isDaily ? 'daily' : 'weekly', true);
                setLogDialogOpen(false);
              }}
            >
              Retry Dispatch Now
            </Button>
          )}
          <Button onClick={() => setLogDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notifications */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbarMessage ? (
          <Alert severity={snackbarMessage.severity} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
            {snackbarMessage.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
}
